import axios from 'axios';
import { getGaClientId } from './analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors (expired/invalid tokens) by clearing localStorage
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide → nettoyer le localStorage
      const token = localStorage.getItem('token');
      if (token) {
        localStorage.removeItem('token');
        console.log('Token expired or invalid - cleared from localStorage');
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data, dossierId = null, tempToken = null) => {
    const params = {};
    if (dossierId) params.dossier_id = dossierId;
    if (tempToken) params.temp_token = tempToken;
    return api.post('/auth/register', data, { params });
  },
  login: (data, dossierId = null, tempToken = null) => {
    const params = {};
    if (dossierId) params.dossier_id = dossierId;
    if (tempToken) params.temp_token = tempToken;
    return api.post('/auth/login', data, { params });
  },
  getMe: () => api.get('/auth/me'),
  magic: (token) => api.post('/auth/magic', null, { params: { token } }),
};

export const dossierAPI = {
  get: (dossierId) => api.get(`/dossiers/${dossierId}`),
  upload: (formData) => {
    return api.post('/dossiers/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getUserDossiers: () => api.get('/dossiers/user/list'),
  downloadFile: (dossierId, fileId) => api.get(`/dossiers/${dossierId}/file/${fileId}`),
  getDemandePieces: (dossierId, force = false) =>
    api.post(`/dossiers/${dossierId}/letter/demande-pieces`, null, force ? { params: { force: true } } : undefined),
  getDenonciationHuissier: (dossierId, force = false) =>
    api.post(`/dossiers/${dossierId}/letter/denonciation-huissier`, null, force ? { params: { force: true } } : undefined),
};

export const paymentAPI = {
  createSession: (dossierId) => api.post('/payment/create-session', null, { params: { dossier_id: dossierId, ga_client_id: getGaClientId() } }),
  confirmPayment: (sessionId, dossierId) => api.post('/payment/confirm', null, { params: { session_id: sessionId, dossier_id: dossierId } }),
};

export const messageAPI = {
  send: (data) => api.post('/messages', data),
  getMessages: (dossierId) => api.get(`/messages/${dossierId}`),
};

export const adminAPI = {
  getAllDossiers: (params) => api.get('/admin/dossiers', { params }),
  addNote: (dossierId, note) => api.post(`/admin/dossiers/${dossierId}/notes`, null, { params: { note } }),
  getStats: () => api.get('/admin/stats'),
  getFilePreview: (dossierId, fileId) => api.get(`/admin/dossiers/${dossierId}/file/${fileId}/preview`),
  getPartialSubmissions: (params) => api.get('/admin/partial-submissions', { params }),
  getLoginAttempts: (params) => api.get('/admin/login-attempts', { params }),
  getPartialFilePreview: (sessionId, fileId) => api.get(`/admin/partial-submissions/${sessionId}/file/${fileId}/preview`),
  retriggerLV3: (dossierId) => api.post(`/admin/retrigger-lv3/${dossierId}`),
  // Audit d'écart de score entre deux dossiers (identifiants complets ou préfixes courts)
  compareDossiers: (a, b) => api.get('/admin/dossiers/compare', { params: { a, b } }),
  // Ajout de pièces complémentaires à un dossier déjà analysé (relance le pipeline
  // sur la totalité des documents, anciens + nouveaux)
  addDossierFiles: (dossierId, files, relaunchPipeline = true) => {
    const form = new FormData();
    Array.from(files).forEach((file) => form.append('files', file));
    form.append('relaunch_pipeline', relaunchPipeline ? 'true' : 'false');
    return api.post(`/admin/dossiers/${dossierId}/files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updatePartialSubmission: (sessionId, data) => api.patch(`/admin/partial-submissions/${sessionId}`, data),
  updateDossierCrm: (dossierId, data) => api.patch(`/admin/dossiers/${dossierId}/crm`, data),
  updateFileRotation: (dossierId, fileId, rotation) =>
    api.patch(`/admin/dossiers/${dossierId}/file/${fileId}/rotation`, { rotation }),

  // Email Templates
  getEmailTemplates: () => api.get('/admin/email-templates'),
  getEmailTemplate: (templateId) => api.get(`/admin/email-templates/${templateId}`),
  createEmailTemplate: (data) => api.post('/admin/email-templates', data),
  updateEmailTemplate: (templateId, data) => api.put(`/admin/email-templates/${templateId}`, data),
  deleteEmailTemplate: (templateId) => api.delete(`/admin/email-templates/${templateId}`),
  sendTestEmail: (templateId, email) => api.post(`/admin/email-templates/${templateId}/test`, null, { params: { email } }),

  // Email Campaigns
  getEmailCampaigns: () => api.get('/admin/email-campaigns'),
  getEmailCampaign: (campaignId) => api.get(`/admin/email-campaigns/${campaignId}`),
  createEmailCampaign: (data) => api.post('/admin/email-campaigns', data),
  updateEmailCampaign: (campaignId, data) => api.put(`/admin/email-campaigns/${campaignId}`, data),
  deleteEmailCampaign: (campaignId) => api.delete(`/admin/email-campaigns/${campaignId}`),

  // Email Logs & Stats
  getEmailLogs: (params) => api.get('/admin/email-logs', { params }),
  getEmailStats: () => api.get('/admin/email-stats'),
  getScheduledEmails: (params) => api.get('/admin/scheduled-emails', { params }),
  cancelScheduledEmail: (scheduledId) => api.delete(`/admin/scheduled-emails/${scheduledId}`),

  // Lawyer management
  createLawyer: (data) => api.post('/admin/lawyers', data),
  getLawyers: () => api.get('/admin/lawyers'),

  // Lead valorization
  updateLead: (dossierId, data) => api.patch(`/admin/dossiers/${dossierId}/lead`, data),
  addLeadCall: (dossierId, data) => api.post(`/admin/dossiers/${dossierId}/lead/calls`, data),

  // Promo codes (marketplace avocats)
  listPromoCodes: () => api.get('/admin/promo-codes'),
  createPromoCode: (data) => api.post('/admin/promo-codes', data),
  deactivatePromoCode: (id) => api.patch(`/admin/promo-codes/${id}/deactivate`),
};

export const partialSubmissionAPI = {
  save: (data) => api.post('/partial-submissions', data),
  markConverted: (sessionId) => api.post(`/partial-submissions/${sessionId}/convert`),
  uploadFiles: (sessionId, files) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    return api.post(`/partial-submissions/${sessionId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const loginAttemptAPI = {
  save: (data) => api.post('/login-attempts', data),
};

export const lawyerAPI = {
  getLeads: (params) => api.get('/lawyer/leads', { params }),
  purchaseLeads: (dossierIds) => api.post('/lawyer/leads/purchase', { dossier_ids: dossierIds }),
  confirmPurchase: (sessionId) => api.post('/lawyer/leads/confirm', null, { params: { session_id: sessionId } }),
  getPurchasedLeads: () => api.get('/lawyer/leads/purchased'),
  downloadFile: (dossierId, fileId) => api.get(`/lawyer/leads/${dossierId}/file/${fileId}`, { responseType: 'blob' }),
};

export const chatbotAPI = {
  save: (data) => api.post('/chatbot-conversations', data),
  complete: (sessionId, outcome) => api.post(`/chatbot-conversations/${sessionId}/complete`, { outcome }),
};

export default api;